/**
 * The sign-in page's browser session rules, shared by the AngularJS login page
 * (assets/js/main.js) and the access island: the cookies it keeps and how long
 * they live, the remembered username, the saved-account picker and the SSO
 * lookup's caching. Writing the cookies is `@dealr/presenter-account`'s.
 */

const HOUR = 3600000;
const DAY = 24 * HOUR;

/** The cookie names the dealer and admin apps read back. */
export const LOGIN_COOKIES = {
  /** The signed-in session body (`/api/login`'s answer, JSON). */
  session: 'auth',
  /** The device id `/api/login` and `/api/changePass` record as `fingerprint`. */
  deviceToken: 'device_token',
  /** The username the sign-in form opens with. */
  username: 'auth_username',
  /** The accounts already signed in on this browser (written by the server). */
  savedAccounts: 'authenticated_users',
} as const;

export type LoginCookie = 'session' | 'deviceToken' | 'username';

export const LOGIN_COOKIE_LIFETIME_MS: Record<LoginCookie, number> = {
  session: 2 * HOUR,
  deviceToken: 365 * DAY,
  username: 60 * DAY,
};

/** When a cookie written at `now` expires. */
export function loginCookieExpiry(cookie: LoginCookie, now: number): Date {
  return new Date(now + LOGIN_COOKIE_LIFETIME_MS[cookie]);
}

/** How long a signed-in tab waits after writing the session cookies before it announces itself and leaves. */
export const SESSION_HANDOFF_DELAY_MS = 100;

/**
 * The username the form opens with: the remembered one, an empty cookie
 * included; null when there is none. Adding an account means signing in as
 * someone else, and the remembered username belongs to the account already
 * signed in.
 */
export function rememberedUsername(cookie: string | null | undefined, opts: { addAccount: boolean }): string | null {
  if (cookie === null || cookie === undefined || opts.addAccount) return null;
  return cookie;
}

/** One entry of the `authenticated_users` cookie: an account already signed in on this browser. */
export interface SavedAccount {
  userId: string;
  token: string;
  type: string | null;
  name: string;
  dealership: string;
  username: string;
  initials: string;
}

function decode(value: unknown): string {
  if (typeof value !== 'string') return '';
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/** The `authenticated_users` cookie (its JSON text or the parsed object) as accounts; entries without a token are skipped. */
export function parseSavedAccounts(raw: unknown): SavedAccount[] {
  let parsed: unknown = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!parsed || typeof parsed !== 'object') return [];
  const accounts: SavedAccount[] = [];
  const entries = parsed as Record<string, Record<string, unknown> | null>;
  for (const userId of Object.keys(entries)) {
    const entry = entries[userId];
    if (!entry || typeof entry['token'] !== 'string') continue;
    accounts.push({
      userId,
      token: entry['token'],
      type: typeof entry['type'] === 'string' ? entry['type'] : null,
      name: decode(entry['name']) || decode(entry['username']) || 'User ' + userId,
      dealership: decode(entry['dealership_name']),
      username: decode(entry['username']),
      initials: decode(entry['initials']),
    });
  }
  return accounts;
}

/** The sign-in page opens on the account picker for two or more saved accounts. */
export function opensAccountPicker(savedAccountCount: number): boolean {
  return savedAccountCount > 1;
}

/**
 * `GET /api/login/samlDetails` failed: a 400 ("Not configured for SAML.")
 * means the username does not sign in through SSO, and is remembered as such;
 * any other failure is asked again next time.
 */
export function samlNotConfigured(status: number): boolean {
  return status === 400;
}

/** The identity provider redirect; a `redirUrl` rides along, which `samlStartLogin` puts in the RelayState. */
export function samlStartUrl(redirect: string, redirUrl: string | null | undefined): string {
  return redirUrl ? redirect + '?redirUrl=' + encodeURIComponent(redirUrl) : redirect;
}
